import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { createSolanaEventListener, BlockchainEvent, SolanaEventListener } from "@/lib/solana-event-listeners";
import { logger } from "@/lib/logger";

interface BlockchainEventContextType {
  eventListener: SolanaEventListener | null;
  isListening: boolean;
  isConnected: boolean;
  error: Error | null;
  reconnectionAttempts: number;
  // Typed subscription methods
  onTradeExecuted: (callback: (event: BlockchainEvent) => void) => () => void;
  onVoteCast: (callback: (event: BlockchainEvent) => void) => () => void;
  onPoolUpdated: (callback: (event: BlockchainEvent) => void) => () => void;
  onTokenCreated: (callback: (event: BlockchainEvent) => void) => () => void;
}

const BlockchainEventContext = createContext<BlockchainEventContextType>({
  eventListener: null,
  isListening: false,
  isConnected: false,
  error: null,
  reconnectionAttempts: 0,
  onTradeExecuted: () => () => {},
  onVoteCast: () => () => {},
  onPoolUpdated: () => () => {},
  onTokenCreated: () => () => {},
});

export function BlockchainEventProvider({ children }: { children: ReactNode }) {
  const [eventListener, setEventListener] = useState<SolanaEventListener | null>(null);
  const [isListening, setIsListening] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [reconnectionAttempts, setReconnectionAttempts] = useState(0);

  const onTradeExecuted = useCallback((callback: (event: BlockchainEvent) => void) => {
    if (!eventListener) return () => {};
    eventListener.onEvent("trade_executed", callback);
    return () => eventListener.offEvent("trade_executed", callback);
  }, [eventListener]);

  const onVoteCast = useCallback((callback: (event: BlockchainEvent) => void) => {
    if (!eventListener) return () => {};
    eventListener.onEvent("vote_cast", callback);
    return () => eventListener.offEvent("vote_cast", callback);
  }, [eventListener]);

  const onPoolUpdated = useCallback((callback: (event: BlockchainEvent) => void) => {
    if (!eventListener) return () => {};
    eventListener.onEvent("pool_updated", callback);
    return () => eventListener.offEvent("pool_updated", callback);
  }, [eventListener]);

  const onTokenCreated = useCallback((callback: (event: BlockchainEvent) => void) => {
    if (!eventListener) return () => {};
    eventListener.onEvent("token_created", callback);
    return () => eventListener.offEvent("token_created", callback);
  }, [eventListener]);

  useEffect(() => {
    let healthCheckInterval: NodeJS.Timeout;
    let reconnectTimeout: NodeJS.Timeout;
    
    const initEventListener = async () => {
      try {
        logger.debug("Initializing Solana event listener...");
        const listener = createSolanaEventListener();
        await listener.startListening();
        setEventListener(listener);
        setIsListening(true);
        setIsConnected(true);
        setReconnectionAttempts(0);
        setError(null);
        logger.info("Solana event listener initialized successfully");
      } catch (err) {
        logger.error("Failed to initialize Solana event listener", err);
        setError(err instanceof Error ? err : new Error("Unknown error"));
        setIsListening(false);
        setIsConnected(false);
        
        if (reconnectionAttempts < 10) {
          const delay = Math.min(1000 * Math.pow(2, reconnectionAttempts), 30000);
          logger.info(`Retrying connection in ${delay}ms`, { attempt: reconnectionAttempts + 1, maxAttempts: 10 });
          reconnectTimeout = setTimeout(() => {
            setReconnectionAttempts(prev => prev + 1);
          }, delay);
        }
      }
    };

    initEventListener();

    healthCheckInterval = setInterval(async () => {
      if (eventListener && isListening) {
        try {
          const connection = eventListener.getConnection();
          const slot = await connection.getSlot();
          
          if (slot > 0) {
            setIsConnected(true);
            setError(null);
          } else {
            throw new Error("Invalid slot received");
          }
        } catch (err) {
          console.error("Health check failed:", err);
          setIsConnected(false);
          setError(err instanceof Error ? err : new Error("Health check failed"));
          
          if (eventListener) {
            eventListener.stopListening();
            setEventListener(null);
            setIsListening(false);
          }
          
          if (reconnectionAttempts < 10) {
            setReconnectionAttempts(prev => prev + 1);
          }
        }
      }
    }, 30000);

    return () => {
      clearInterval(healthCheckInterval);
      clearTimeout(reconnectTimeout);
      if (eventListener) {
        eventListener.stopListening();
        setEventListener(null);
        setIsListening(false);
        setIsConnected(false);
      }
    };
  }, [reconnectionAttempts]);

  return (
    <BlockchainEventContext.Provider value={{
      eventListener,
      isListening,
      isConnected,
      error,
      reconnectionAttempts,
      onTradeExecuted,
      onVoteCast,
      onPoolUpdated,
      onTokenCreated
    }}>
      {children}
    </BlockchainEventContext.Provider>
  );
}

export function useBlockchainEvents() {
  const context = useContext(BlockchainEventContext);
  if (context === undefined) {
    throw new Error("useBlockchainEvents must be used within a BlockchainEventProvider");
  }
  return context;
}